import React from "react";
import {Button, Dropdown, DropdownButton, Form} from "react-bootstrap";
import {BsCheck2} from "react-icons/bs";
import {useGuestContentFavorite} from "../../../hooks/useGuestContentFavorite";
import {identity} from "../../../utils/helpers";
import {useListsQuery} from "../../../hooks/useListsQuery";
import {ErrorBoundary} from "@sentry/react";
import {FormProvider, UseFormReturn} from "react-hook-form";
import {htmlTagsRegex} from "../../../utils/regularExpressions";
import {TextBox} from "../../../components/textBox";
import {InView} from "react-intersection-observer";
import {FaHeart, FaRegHeart} from "react-icons/fa";
import {AiOutlineClose, AiOutlinePlus} from "react-icons/ai";

export const Favorites = () => {
    const {isAuthenticated} = identity();

    // Only query for lists if the user is authenticated to avoid unnecessary API calls
    if (isAuthenticated) {
        return (
            <InView triggerOnce={true}>
                {({inView, ref}) => (
                    <ErrorBoundary fallback={<></>}>
                        <div ref={ref}>
                            {inView && <AuthenticatedContent/>}
                        </div>
                    </ErrorBoundary>
                )}
            </InView>
        );
    }

    return <GuestContent/>;
};

const GuestContent = () => {
    const {show, onToggle, loginUrl, registerUrl} = useGuestContentFavorite();
    return (
        <div id="favorites-dropdown" className="h-100">
            <DropdownButton
                size='lg'
                variant="link"
                title={<FaRegHeart size={25} className="text-dark"/>}
                show={show}
                onToggle={onToggle}
                align='end'
                aria-label="Add to Favorites"
            >
                <Dropdown.Header>
                    Please log in or create an account to add to favorites.
                </Dropdown.Header>
                <Dropdown.ItemText>
                    <Button variant="primary" className="w-100" href={loginUrl}>
                        Log In
                    </Button>
                </Dropdown.ItemText>
                <Dropdown.ItemText>
                    <Button variant="outline-primary" className="w-100" href={registerUrl}>
                        Create Account
                    </Button>
                </Dropdown.ItemText>
            </DropdownButton>
        </div>
    );
}

const AuthenticatedContent = () => {
    const {
        state: {
            loading,
            error
        },
        form: {
            form,
            show: showForm,
            onToggle: setShowForm,
            onSubmit,
            state: {
                isValid,
                isSubmitting
            }
        },
        dropDownButton: {
            title,
            show,
            onToggle
        },
        dropDownItem: {
            toggleItem
        },
        model: {
            lists,
            isMatch
        }
    } = useListsQuery();

    if (error) return <></>;

    return (
        <div id="favorites-dropdown" className="h-100">
            <DropdownButton
                size='lg'
                variant="link"
                title={title}
                show={show}
                onToggle={onToggle}
                disabled={loading}
                align='end'
                aria-label="Add to Favorites"
                aria-expanded={show}
            >
                <Dropdown.Header
                    className="border-bottom sticky-top bg-white px-0">
                    <div className="d-flex align-items-center justify-content-between px-3">
                        <div>Save to list:</div>
                        <Button variant="link" size="sm" className="float-right fw-bold d-flex align-items-center"
                                onClick={setShowForm} aria-label={showForm ? "Cancel" : "Create New List"}>
                            {showForm ? <><AiOutlineClose className="me-1" size={18} aria-hidden/>Cancel</> : <>
                                <AiOutlinePlus className="me-1" size={18} aria-hidden/>Create New List</>}
                        </Button>
                    </div>
                    <ListForm form={form}
                              showForm={showForm}
                              onSubmit={onSubmit}
                              isSubmitting={isSubmitting}
                              isValid={isValid}
                    />
                </Dropdown.Header>


                {lists.map(({id, name, path}) => (
                    <Dropdown.Item key={id} as="button" href={path} aria-labelledby={`list-label-${name}-${id}`}
                                   className=" btn btn-link py-3 d-flex align-items-center justify-content-between border-top border-bottom"
                                   onClick={() => toggleItem(id)}>
                        <div id={`list-label-${name}-${id}`}>{name} </div>
                        {isMatch(id) ?

                            <div className="position-relative">
                                <FaHeart size={24} className="text-dark" aria-hidden="true"/>
                                <BsCheck2
                                    className="position-absolute top-50 start-50 translate-middle text-white"
                                    size={20}
                                    aria-label="Item is in favorites" // Add alt text for the checkmark icon
                                />
                            </div>
                            : <FaRegHeart size={24} className="text-dark" aria-hidden="true"/>}
                    </Dropdown.Item>
                ))}
                <Dropdown.ItemText className="py-3 bg-white sticky-bottom border-top">
                    <Button href='/WishList/WishLists' variant="outline-dark" className="w-100">
                        Manage My Lists & Favorites
                    </Button>
                </Dropdown.ItemText>
            </DropdownButton>
        </div>
    );
}

const ListForm = ({showForm, form, onSubmit, isSubmitting, isValid}: {
    showForm: boolean,
    form: UseFormReturn<{ name: string }>,
    onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>,
    isSubmitting: boolean,
    isValid: boolean
}) => {
    return (
        <>
            {showForm && (
                <FormProvider {...form}>
                    <Form className="my-3 px-3" onSubmit={onSubmit}>
                        <Form.Group controlId="listName">
                            <TextBox
                                fieldName="name"
                                displayName="List Name"
                                minLength={1}
                                maxLength={65}
                                required={true}
                                className="mb-2"
                                disabled={isSubmitting}
                                validation={{
                                    enabled: true,
                                    rules: {
                                        checkForHtmlTags: (value: string) => {
                                            // If the value is empty, return true
                                            if (value.length < 1) return true;

                                            // If the value is not empty and contains HTML tags, return error message
                                            if (htmlTagsRegex.test(value)) return 'HTML tags are not allowed in this field.';

                                            // If the value is not empty and does not contain HTML tags, return true to pass validation
                                            return true;
                                        }
                                    }
                                }}
                            />
                        </Form.Group>
                        <Button variant="success"
                                className="btn-sm w-100 mt-2"
                                type="submit"
                                disabled={!isValid || isSubmitting}>
                            Save
                        </Button>
                    </Form>
                </FormProvider>
            )}
        </>
    );
};